import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiYoutube, FiTrash2, FiEdit } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import api from '../../../../api/api';

const VideosTab = () => {
  const [videos, setVideos] = useState([]);
  const [isAddingVideo, setIsAddingVideo] = useState(false);
  const [videoForm, setVideoForm] = useState({
    title: '',
    video_url: ''
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await api.getYoutubeVideos();
      setVideos(Array.isArray(response) ? response : [response]);
    } catch (error) {
      toast.error('Failed to fetch videos');
      console.error('Error fetching videos:', error);
    }
  };

  const getYoutubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const validateYoutubeUrl = (url) => {
    const videoId = getYoutubeVideoId(url);
    return videoId !== null;
  };

  const handleAddVideo = async () => {
    if (!videoForm.title.trim()) {
      toast.error('Please enter a video title');
      return;
    }

    if (!validateYoutubeUrl(videoForm.video_url)) {
      toast.error('Please enter a valid YouTube URL');
      return;
    }

    try {
      await api.addYoutubeVideo({
        title: videoForm.title,
        video_url: videoForm.video_url
      });

      await fetchVideos(); // Refresh the list
      setVideoForm({ title: '', video_url: '' });
      setIsAddingVideo(false);
      toast.success('Video added successfully');
    } catch (error) {
      toast.error('Failed to add video');
      console.error('Error adding video:', error);
    }
  };

  const VideoCard = ({ video }) => {
    const videoId = getYoutubeVideoId(video.video_url);
    const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '';

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm overflow-hidden"
      >
        <div className="aspect-video relative">
          <img
            src={thumbnailUrl}
            alt={video.title}
            className="w-full h-full object-cover"
          />
          <a
            href={video.video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity"
          >
            <FiYoutube className="text-4xl text-red-500" />
          </a>
        </div>
        <div className="p-4">
          <h3 className="font-medium text-gray-900 truncate">{video.title}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {new Date(video.created_at).toLocaleDateString()}
          </p>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <button
        onClick={() => setIsAddingVideo(true)}
        className="bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 transition-colors"
      >
        Add Video
      </button>

      {isAddingVideo && (
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Video Title"
              value={videoForm.title}
              onChange={(e) => setVideoForm(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
            />
            <input
              type="text"
              placeholder="YouTube URL"
              value={videoForm.video_url}
              onChange={(e) => setVideoForm(prev => ({ ...prev, video_url: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
            />
            <div className="flex space-x-2">
              <button
                onClick={handleAddVideo}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
              >
                Save
              </button>
              <button
                onClick={() => setIsAddingVideo(false)}
                className="bg-gray-200 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {videos.map((video) => (
          <VideoCard key={video.id} video={video} />
        ))}
      </div>
    </div>
  );
};

export default VideosTab;
